import * as React from 'react';
import { IFlyoutProps } from './flyout-props';
import { FlyoutContainer } from './parts';
import { isTargetElementUnderContainer } from '../../utils';
import { withHostablePanel, IWithHostableSurfaceProps } from '../hostable-surface';
import { Modal } from '../modal';

interface IFlyoutContentBaseProps extends Pick<IFlyoutProps,
    'animationDuration' | 'className' | 'flyoutPosition' | 'hidden' |
    'onRenderFooterContent' | 'footerAlignment' | 'preventDismissOnFocusOut' | 'showModal' |
    'size' | 'title' | 'decorator' | 'decoratorProps' | 'showTitleSeparator'>, IWithHostableSurfaceProps {
    children?: React.ReactNode;
    isClosingDialog?: boolean;
    onCloseFlyout(): void;
}

const FlyoutContentBase = React.memo((props: IFlyoutContentBaseProps) => {
    const {
        className, showModal, children, animationDuration = 250, flyoutPosition = 'right',
        size = 'small', onRenderFooterContent, footerAlignment, title, isClosingDialog, onCloseFlyout,
        addSurfaceEventListener, removeSurfaceEventListener, preventDismissOnFocusOut,
        decorator = 'div', decoratorProps = {}, showTitleSeparator
    } = props;

    const flyoutContainerRef = React.useRef<HTMLDivElement>(null);

    const _onSurfaceClickHandler = React.useCallback((ev: MouseEvent) => {
        const isTargetInFlyout = isTargetElementUnderContainer(flyoutContainerRef, ev.target as Node);
        if (isTargetInFlyout) {
            return;
        }
        onCloseFlyout();
    }, [onCloseFlyout]);

    React.useEffect(() => {
        if (preventDismissOnFocusOut) {
            removeSurfaceEventListener('surfaceClick', _onSurfaceClickHandler);
            return;
        }
        addSurfaceEventListener('surfaceClick', _onSurfaceClickHandler);

        return () => {
            removeSurfaceEventListener('surfaceClick', _onSurfaceClickHandler);
        };
    }, [_onSurfaceClickHandler, addSurfaceEventListener, preventDismissOnFocusOut, removeSurfaceEventListener]);

    return (
        <React.Fragment>
            {showModal ? <Modal animationDuration={animationDuration} isClosing={isClosingDialog} /> : null}
            <FlyoutContainer elementRef={flyoutContainerRef} animationDuration={animationDuration}
                size={size} onCloseFlyout={onCloseFlyout} decorator={decorator} decoratorProps={decoratorProps}
                className={className} flyoutPosition={flyoutPosition} onRenderFooterContent={onRenderFooterContent}
                footerAlignment={footerAlignment} title={title} isClosing={isClosingDialog} showTitleSeparator={showTitleSeparator}>
                {children}
            </FlyoutContainer>
        </React.Fragment>
    );
});

FlyoutContentBase.displayName = 'FlyoutContent';

export const FlyoutContent = withHostablePanel(FlyoutContentBase);
